import clsx from 'clsx';
import ms from 'ms';
import tinycolor from 'tinycolor2';
import { UAParser } from 'ua-parser-js';
import {
  chainIds,
  ContractMethod,
  CreditRoleEnum,
  DEPLOY_ENVIRONMENT,
  ENVIRONMENT,
  getChainName,
  IS_PRODUCTION_DEPLOYMENT,
  THIRD_PARTY_CONTRACTS,
} from '@soundxyz/utils/const';
import { ExtendedContractMethod } from '@context/transaction/schema';
import { MintingAccessConfigMintingType } from '@graphql/reactQuery';

// Limit avatar images to 2mb
export const MAX_AVATAR_IMAGE_SIZE = 2000000;

export const NEW_DESCRIPTION_MAX_CHARS = 144;
export const DISPLAY_NAME_MAX_CHARS = 20;

export const COMMENT_MAX_CHARS = 280;

export const MAX_UINT32 = 4294967295;

export const SPOTIFY_BASE_URL = 'https://open.spotify.com/artist/';

export const OPENSEA_BASE_URL = 'https://opensea.io/collection/';

export const GENRE_NAME_OVERRIDES = ['R&B & Soul'];

export const txProgressMessages: { [key in ExtendedContractMethod]: string } = {
  [ContractMethod.ARTIST__BUY_EDITION]: 'Transaction initiated!',
  [ContractMethod.SAM__BUY_EDITION]: 'Transaction initiated!',
  [ContractMethod.SAM__SELL_EDITION]: 'Transaction initiated!',
  [ContractMethod.ARTIST__WITHDRAW_FUNDS]: 'Withdrawal from artist contract initiated',
  [ContractMethod.SPLIT_MAIN__CREATE_SPLIT]: 'Split creation initiated!',
  [ContractMethod.DRAFT_SPLIT_MAIN__CREATE_SPLIT]: 'Split creation initiated!',
  [ContractMethod.SPLIT_MAIN__DISTRIBUTE_ETH]: 'Splitting initiated!',
  [ContractMethod.SPLIT_MAIN__WITHDRAW]: 'Withdrawal initiated!',
  [ContractMethod.SOUND_CREATOR__CREATE_SOUND_AND_MINTS]: 'Sound creation initiated',
  [ContractMethod.SOUND_EDITION__WITHDRAW_ETH]: 'Withdrawal from edition initiated',
  [ContractMethod.MULTICALL__WITHDRAW]: 'Withdrawal initiated',
  [ContractMethod.DRAFT_SOUND_CREATOR__CREATE_SOUND_AND_MINTS]: 'Sound creation initiated',
  [ExtendedContractMethod.ENABLE_AFFILIATE_FEE]: 'Affiliate fee update initiated',
  [ExtendedContractMethod.DISABLE_AFFILIATE_FEE]: 'Affiliate fee update initiated',
  [ExtendedContractMethod.MULTICALL__EDIT]: 'Release changes initiated',
  [ContractMethod.AFFILIATE__WITHDRAW]: 'Affiliate withdrawal initiated',
  [ContractMethod.COLLECTOR__TRANSFER_EDITION]: 'Transfer initiated!',
  [ExtendedContractMethod.SEND_FUNDS]: 'Transfer initiated!',
};

export const MINUTE = 60,
  HOUR = MINUTE * 60,
  DAY = HOUR * 24,
  WEEK = DAY * 7,
  MONTH = DAY * 30,
  YEAR = DAY * 365;

export const SHELF_RELEASE_DATE_FORMAT = 'LLL dd, y';

// TODO: keeping this lower as 10k right now, since we don't have per mint fee
// https://linear.app/sound/issue/ENG-7433/mint-quantity-upper-limit-removal
export const ADDRESS_BATCH_MINT_LIMIT = 10_000;

///////////// CHAIN HELPERS

export const isMainSoundxyzSite =
  !!process.env.NEXT_PUBLIC_WWW_SOUND_XYZ ||
  (typeof window !== 'undefined' && window.location.hostname === 'www.sound.xyz');

export const MAX_OWNERS = 100;

export const SPLIT_ALLOCATION_SCALE = 1e6;

export const EMPTY_SIGNATURE =
  '0x00000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000';

export const NAV_LINKS = {
  Home: '/',
  Feed: '/feed',
  Charts: '/charts',
  TopCharts: '/charts/top',
  ViralChartsSounds: '/charts/viral/sounds',
  ViralChartsPlaylists: '/charts/viral/playlists',
  Releases: '/dashboard/releases',
  Upload: '/secret/dashboard/upload',
  UploadV2: '/dashboard/upload',
  Edit: '/dashboard/edit',
  About: '/about',
  Earnings: '/dashboard/earnings',
  Referrals: '/dashboard/earnings?tab=referrals',
  ProfileEdit: '/dashboard/profile',
  NotificationsDashboard: '/dashboard/email',
  Community: '/dashboard/community',
  Upcoming: '/upcoming',
  Recent: '/recent',
  SignIn: '/signin',
  Search: '/search',
  Explore: '/explore',
  ArtistApplication: '/artist-application',
  AdminArtistApplications: '/admin/artist-applications',
  NotificationPage: '/notifications',
  TopArtists: '/charts/top/artists/7d/total-volume',
  TopCollectors: '/charts/top/collectors/7d/total-spent',
  TopCurators: '/charts/top/curators/7d/total-volume',
  TopSongs: '/charts/top/sounds/7d/total-volume',
} as const;

/**
 * Should only be used for absolute URLs to external sites
 */
export const EXTERNAL_PATHS = {
  Blog: 'https://sound.mirror.xyz/',
  Careers: 'https://jobs.ashbyhq.com/sound.xyz',
  Docs: 'https://docs.sound.xyz',
  Discord: 'https://discord.gg/soundxyz',
  Instagram: 'https://www.instagram.com/soundxyz/',
  Market: 'https://sound.xyz/market',
  Privacy: 'https://soundxyz.notion.site/Privacy-Policy-dfa7fc4039444f0a86c4049450872a65',
  TOS: 'https://soundxyz.notion.site/Terms-of-Service-782b54520f0047aca0afb81552e3b7fb',
  Twitter: 'https://twitter.com/soundxyz_',
} as const;

/**
 * Should only be used for help.sound.xyz URLs
 */
export const HELP_PATHS = {
  ArtistApplicationAbout: 'https://help.sound.xyz/hc/en-us/articles/15739741984539',
  ArtistApplicationWalletLogin: 'https://help.sound.xyz/hc/en-us/articles/17819125327643',
  ArtistFaq: 'https://help.sound.xyz/hc/en-us/categories/5404751412763-Artists',
  DropTypeFaq: 'https://help.sound.xyz/hc/en-us/articles/15738590427931',
  RequestHelp: 'https://help.sound.xyz/hc/en-us/requests/new',
  Home: 'https://help.sound.xyz',
} as const;

const HEADER_PATHS = {
  Song: '/[soundHandle]/[releaseSlug]',
  Playlist: '/playlist/[id]',
  ArtistProfile: '/[soundHandle]',
  UserProfile: '/user/[publicAddress]',
  ArtistCollectors: '/[soundHandle]/collectors',
  ArtistCollection: '/[soundHandle]/collection',
  ArtistReleases: '/[soundHandle]/releases',
  ArtistAppearsOn: '/[soundHandle]/appears-on',
  ArtistPlaylists: '/[soundHandle]/playlists',
  ArtistActivity: '/[soundHandle]/activity',
  UserProfileSection: '/user/[publicAddress]/[profileSection]',
  Landing: '/',
};

export const BLUR_BACKGROUND_PATHS = [
  HEADER_PATHS.ArtistProfile,
  HEADER_PATHS.UserProfile,
  HEADER_PATHS.Song,
  NAV_LINKS.Home,
  HEADER_PATHS.Playlist,
  HEADER_PATHS.ArtistCollectors,
  HEADER_PATHS.ArtistCollection,
  HEADER_PATHS.ArtistReleases,
  HEADER_PATHS.ArtistAppearsOn,
  HEADER_PATHS.ArtistPlaylists,
  HEADER_PATHS.ArtistActivity,
  HEADER_PATHS.UserProfileSection,
  HEADER_PATHS.Landing,
  NAV_LINKS.ArtistApplication,
  NAV_LINKS.About,
];

export const AnalyticsPageCategories = {
  LANDING: 'LandingPage',
  LANDINGV2: 'LandingPageV2',
  ARTIST: 'ArtistPage',
  SONG: 'SongPage',
  PRESALE: 'PresalePage',
  COLLECTOR: 'CollectorPage',
  EXPLORE: 'ExplorePage',
  TRENDING: 'TrendingPage', // @deprecated -> moved to Charts
  VIRAL_CHARTS: 'ViralChartsPage',
  TOP_CHARTS: 'TopChartsPage',
  LATEST: 'LatestPage',
  EDIT_PROFILE: 'EditProfilePage',
  EDIT_PROFILE_FEATURED_SHELVES: 'EditProfileFeaturedShelves',
  EDIT_SHELF: 'EditShelfPage',
  CREATE_SHELF: 'CreateShelfPage',
  UPLOAD_RELEASE: 'UploadReleasePage',
  ARTIST_RELEASES_DASHBOARD: 'ArtistReleasesDashboardPage',
  ACTIVITY_FEED: 'ACTIVITY_FEED',
  PLAYLIST: 'Playlist',
  PLAYLISTS: 'Playlists',
  COLLECTION: 'Collection',
  LIKED_SOUNDS: 'LikedSongs',
  COLLECTORS: 'Collectors',
  ARTIST_RELEASES: 'ArtistReleases',
  ARTISTS_BACKED: 'ArtistsBacked',
  APPEARS_ON: 'AppearsOn',
  ARTIST_ACTIVITY: 'ArtistActivity',
  COLLECTOR_ACTIVITY: 'CollectorActivity',
  VERIFY_NOTIFICATION_EMAIL: 'VerifyNotificationEmail',
  ARTIST_APPLICATION: 'ArtistApplication',
  NOTIFICATIONS: 'Notifications',
} as const;

export const ACCEPTED_MIME_TYPES = {
  'audio/wav': ['.wav'],
  'audio/x-wav': ['.x-wav'],
  'audio/mpeg': ['.mp3'],
  'audio/vnd.wave': ['.wav'],
};

export const ACCEPTED_IMAGE_TYPES = {
  'image/png': ['.png'],
  'image/jpg': ['.jpg'],
  'image/jpeg': ['.jpeg'],
};

export const ACCEPTED_IMAGE_TYPES_INCLUDING_GIF = {
  ...ACCEPTED_IMAGE_TYPES,
  'image/gif': ['.gif'],
};

export const splitMainAddress = THIRD_PARTY_CONTRACTS.splitMain;
export const multicallAddress = THIRD_PARTY_CONTRACTS.multicall3;

export const WAVEFORM_AVATAR_SIZE = 24;
export const WAVEFORM_AVATAR_SIZE_COLLAPSED = 4;

export const SPACE = `\u00A0`;

export const DASH = `\u2014`;

export const SONG_DEFAULT_REWARDS = {
  GOLDEN_EGG: 'GOLDEN_EGG',
  COMMENT: 'COMMENT',
} as const;

export const SONG_PAGE_REWARDS = {
  [SONG_DEFAULT_REWARDS.GOLDEN_EGG]: SONG_DEFAULT_REWARDS.GOLDEN_EGG,
  AUDIO_DOWNLOAD: 'AUDIO_DOWNLOAD',
  [SONG_DEFAULT_REWARDS.COMMENT]: SONG_DEFAULT_REWARDS.COMMENT,
} as const;

export const ALBUM_DEFAULT_REWARDS = {
  GOLDEN_EGG: 'GOLDEN_EGG',
  COMMENT: 'COMMENT',
} as const;

export const REWARDS_TITLE = {
  GOLDEN_EGG: 'Golden Egg',
  COMMENT: 'Comment Wall',
} as const;

export type SongDefaultRewards = (typeof SONG_DEFAULT_REWARDS)[keyof typeof SONG_DEFAULT_REWARDS];
export type AlbumDefaultRewards =
  (typeof ALBUM_DEFAULT_REWARDS)[keyof typeof ALBUM_DEFAULT_REWARDS];

export const SPLITS_ROLES = [
  {
    label: 'Artist',
    value: CreditRoleEnum.ARTIST,
  },
  {
    label: 'Producer',
    value: CreditRoleEnum.PRODUCER,
  },
  {
    label: 'Songwriter',
    value: CreditRoleEnum.SONGWRITER,
  },
  {
    label: 'Curator',
    value: CreditRoleEnum.CURATOR,
  },
  {
    label: 'Remixer',
    value: CreditRoleEnum.REMIXER,
  },
  {
    label: 'Visual Artist',
    value: CreditRoleEnum.VISUAL_ARTIST,
  },
  {
    label: 'Other',
    value: CreditRoleEnum.OTHER,
  },
];

export type RoleFieldType = (typeof SPLITS_ROLES)[0];

export type SplitType = {
  ownerAddress: string;
  roles: CreditRoleEnum[];
  percent: string | number;
};

export const SPLITS_DECIMALS = 2;

export const getAllowlistDisplayName = ({
  mintType,
}: {
  mintType: MintingAccessConfigMintingType;
}) => {
  return mintType === MintingAccessConfigMintingType.Free ? 'Free Mint' : 'Presale';
};
export enum SingleUploadStep {
  ReleaseInfo = 1,
  CreditsAndSplits = 2,
  DropConfiguration = 3,
  Review = 4,
}

export enum UPLOAD_STEP_NAMES_OPEN_UP_SOUND {
  RELEASE_INFO = 'Release Info',
  ALBUM_INFO = 'Album Info',
  SPLITS = 'Splits',
  DROP_CONFIGURATION = 'Drop Configuration',
  REVIEW_AND_DEPLOY = 'Review & Upload',
}

export const UPLOAD_STEPS_OPEN_UP_SOUND = [
  {
    name: UPLOAD_STEP_NAMES_OPEN_UP_SOUND.RELEASE_INFO,
    step: SingleUploadStep.ReleaseInfo,
    optional: false,
  },
  {
    name: UPLOAD_STEP_NAMES_OPEN_UP_SOUND.SPLITS,
    step: SingleUploadStep.CreditsAndSplits,
    optional: false,
  },
  {
    name: UPLOAD_STEP_NAMES_OPEN_UP_SOUND.DROP_CONFIGURATION,
    step: SingleUploadStep.DropConfiguration,
    optional: false,
  },
  {
    name: UPLOAD_STEP_NAMES_OPEN_UP_SOUND.REVIEW_AND_DEPLOY,
    step: SingleUploadStep.Review,
    optional: false,
  },
];

// Upload tool step IDs
export const RELEASE_INFO_STEP_ID = SingleUploadStep.ReleaseInfo;
export const SPLITS_STEP_ID = SingleUploadStep.CreditsAndSplits;
export const DROP_CONFIG_STEP_ID = SingleUploadStep.DropConfiguration;
export const REVIEW_AND_DEPLOY_STEP_ID = SingleUploadStep.Review;

export const TEMPORARY_SUCCESS_STATE_MS = 3000;

export const COMMENT_REWARD_DESCRIPTION =
  'Make a public comment on the song. Prove you were a supporter from way back in the day. Sell your NFT, and your comment is replaceable by the new owner.';

export const COLLECTORS_DOWNLOAD_AUDIO_COPY =
  'Anyone who collects your release can download the uncompressed lossless audio file for this song.';

export const RELEASE_DESCRIPTION_COPY =
  'Share 1-2 paragraphs about the story and history behind your release...';

export const TOKEN_SYMBOL_TOOLTIP_COPY =
  'The token symbol will be displayed on Etherscan when others view your smart contract. We recommend using your song name.';

export const LEARN_MORE_ABOUT_SAM = 'https://help.sound.xyz/hc/en-us/articles/13099028261659';
export const LEARN_MORE_SOUND_FEE =
  'https://help.sound.xyz/hc/en-us/articles/9003588815387-Does-Sound-Take-A-Fee-';
export const LEARN_MORE_TIERS = 'https://help.sound.xyz/hc/en-us/articles/17543164476443';

export const SAM_EGG_GAME_FEE_BPS = 0;
export const SAM_ENABLED_AFFILIATE_FEE_BPS = 250;
export const SAM_DISABLED_AFFILIATE_FEE_BPS = 0;

/** In case there is an error, we set a timeout to reset for collect button */
export const RE_ENABLE_COLLECT_TIMEOUT = 5_000;

// TODO: consider removing this.
export const SAM_DEFAULT_ENABLED = true;

export const SDK_SOUND_ATTRIBUTON_ID =
  100034050823429212663744024974472219327326309225212339598659123908725914828493n;

/**
 * Key for https://www.onramper.com
 *
 * Note: we only have this set in production because there is no sandbox for onramper.
 */
export const ONRAMPER_KEY = process.env.NEXT_PUBLIC_ONRAMPER_KEY;

// STATSIG FEATURE GATES
export const FEATURE_GATES = {
  SOUND_SWAP_CREDIT_CARD_CHECKOUT: 'sound_swap_credit_card_checkout', // TODO: Consolidate https://linear.app/sound/issue/ENG-6308/consolidate-paper-checkout-to-one-feature-flag
  MINTERS_CREDIT_CARD_CHECKOUT: 'minters_credit_card_checkout', // TODO: Consolidate https://linear.app/sound/issue/ENG-6308/consolidate-paper-checkout-to-one-feature-flag
  PAPER_ETH_CHECKOUT: 'paper_checkout_with_eth',
  ACTIVITY_FEED_SUBSCRIPTIONS: 'activity_feed_subscriptions',
  TOP_AUDIENCE_SUBSCRIPTIONS: 'top_audience_subscriptions',
  RELEASE_MINT_DATA_SUBSCRIPTIONS: 'release_mint_data_subscriptions',
  RELEASE_MINIMAL_SUBSCRIPTIONS: 'release_minimal_subscriptions',
  RELEASE_SALE_DETAILS_SUBSCRIPTIONS: 'release_sale_details_subscriptions',
  RELEASE_NFTS_SUBSCRIPTIONS: 'release_nfts_subscriptions',
  RELEASE_NFTS_COMMENTS_SUBSCRIPTIONS: 'release_nfts_comments_subscriptions',
  FORCE_RELEASE_INFO_EDIT: 'force_release_info_edit',
  ARTIST_ALBUM_UPLOAD_ACCESS: 'artist_album_upload_access',
  CROSSMINT_CC_CHECKOUT: 'crossmint_credit_card_checkout',
  BUY_ON_SOUND_MARKET: 'buy_on_sound_market',
  SHOULD_OPTIMIZE_DEPLOY_SPLITS: 'should_optimize_deploy_splits',
  COMMENT_MODAL_V2: 'comment_modal_v2',
  SOUND_SWAP_UPLOAD: 'sound_swap_upload',
  EDIT_RELEASE_V2: 'edit_release_v2',
  EDIT_TIERED_RELEASE: 'edit_tiered_release',
  SONG_PAGE_COMMENT_SECTION: 'song_page_comment_section',
  NAVIGATION_V2: 'navigation_v2',
  REVAMP_AUDIENCE: 'revamp_audience',
  SPONSORED_UPLOAD: 'sponsored_upload',
  ARTIST_EMAIL_WALLET_SIGNUP: 'artist_email_wallet_singup',
  REPLY_POST_COMMENT: 'reply_post_comment',
  UPVOTE_POST_COMMENT: 'upvote_post_comment',
  LISTENING_PARTY_PUBLIC_SALE_START: 'listening_party_public_sale_start',
  GIFTING_MODAL: 'gifting_modal',
} as const;

export const PRESALE_MIN_SUPPLY = 1;

export const INSTAGRAM_CLIENT_ID = (() => {
  //should panic build
  if (!process.env.NEXT_PUBLIC_INSTAGRAM_CLIENT_ID) {
    throw new Error('NEXT_PUBLIC_INSTAGRAM_CLIENT_ID is not defined');
  }
  return process.env.NEXT_PUBLIC_INSTAGRAM_CLIENT_ID;
})();

export const TWITTER_CLIENT_ID = (() => {
  //should panic build
  if (!process.env.NEXT_PUBLIC_TWITTER_CLIENT_ID) {
    throw new Error('NEXT_PUBLIC_TWITTER_CLIENT_ID is not defined');
  }
  return process.env.NEXT_PUBLIC_TWITTER_CLIENT_ID;
})();

export const IS_LOCAL_HOST =
  typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

export const REPORT_ISSUE = 'https://help.sound.xyz/hc/en-us/requests/new';

export const AVAILABLE_CHAIN_IDS = (() => {
  if (IS_PRODUCTION_DEPLOYMENT) {
    return {
      eth: chainIds.MAINNET,
      optimism: chainIds.OPTIMISM,
    };
  }
  return {
    eth: chainIds.GOERLI,
    optimism: chainIds.OPTIMISM_GOERLI,
  };
})();

export const AVAILABLE_CHAIN_IDS_LIST = Object.values(AVAILABLE_CHAIN_IDS);

export const PRIMARY_CHAIN_ID = IS_PRODUCTION_DEPLOYMENT ? chainIds.MAINNET : chainIds.GOERLI;

export const RPC_URLS = (() => {
  const ETH_KEY = process.env.NEXT_PUBLIC_ALCHEMY_ETH_KEY;
  const OP_KEY = process.env.NEXT_PUBLIC_ALCHEMY_OP_KEY;
  //should panic build
  if (!ETH_KEY) {
    throw new Error('NEXT_PUBLIC_ALCHEMY_ETH_KEY is not defined');
  }
  //should panic build
  if (!OP_KEY) {
    throw new Error('NEXT_PUBLIC_ALCHEMY_OP_KEY is not defined');
  }

  if (IS_PRODUCTION_DEPLOYMENT) {
    return {
      eth: [`https://eth-mainnet.g.alchemy.com/v2/${ETH_KEY}`],
      optimism: [`https://opt-mainnet.g.alchemy.com/v2/${ETH_KEY}`],
    };
  }
  return {
    eth: [`https://eth-goerli.g.alchemy.com/v2/${OP_KEY}`],
    optimism: [`https://opt-goerli.g.alchemy.com/v2/${OP_KEY}`],
  };
})();

export const LEARN_MORE_ABOUT_CHAINS = 'https://help.sound.xyz/hc/en‒us/articles/16099549672347';

/**
 * The chain ID that is used when we don't know what chain we are on and for UI purposes
 * we need to show something.
 *
 * This avoids us having to not report to sentry explicitly when we don't know the chain ID.
 */
export const UNKNOWN_CHAIN_ID = 0;

export const DROP_TYPE_COPY = {
  SOUND_SWAP:
    'Starts as an Open Edition. After, price automatically increases as more editions are collected.',
  OPEN_EDITION: 'An unlimited supply of editions can be collected within a specified timeframe.',
  FIXED_EDITION: 'A fixed supply of editions can be collected until all editions are purchased.',
};

export const IS_SERVER = typeof window === 'undefined';

export const WITHDRAW_LEARN_MORE_URL = 'https://help.sound.xyz/hc/en-us/articles/16099549672347';
export const SPLITS_WITHDRAW_LEARN_MORE_URL =
  'https://soundxyz.notion.site/Why-am-I-withdrawing-funds-from-splits-outside-of-my-Sound-releases-9be21d605d3e4c18abf06c6f29a0100c';

export const ASSUMED_HEIGHT_HEADER_FOOTER_MODAL_PX = 452;

export const uaParser = new UAParser();

export const DEFAULT_ERROR_TOAST_MESSAGE_TRY_AGAIN_LATER =
  'Something went wrong. Please try again later.';

export const DEFAULT_DOMINANT_BACKGROUND_COLOR = '#404040';

const DARK_DOMINANT_COLOR = '#1c1a1c';
export const DARK_DOMINANT_COLOR_BRIGHTNESS = tinycolor(DARK_DOMINANT_COLOR).getBrightness();

export const SOUND_MARKET_PLACE_URL = 'https://market.sound.xyz/';

export const CLIENT_QUERY_REDUCTION_ON_FRESH_SSR_STRATEGY = {
  staleTime: ms('1 minute'),
};

export function getCrossMintContractClientIds(chainId: number): {
  merkleDropMinterV2: string;
  merkleDropMinterV2_1: string;
  rangeEditionMinterV2: string;
  rangeEditionMinterV2_1: string;
  soundSwap: string;
  soundSwapV1_1: string;
  /**
   * Optimism does not have a minter adapter
   */
  minterAdapter: string | null;
} {
  const chain = getChainName(chainId);
  switch (chain) {
    case 'GOERLI':
      return {
        rangeEditionMinterV2: '4b4bbdbc-8f85-4c2b-88d4-12cc21729c51',
        merkleDropMinterV2: '550f5357-b427-4474-9203-c8736fda1ee7',
        minterAdapter: '61963be8-0a52-497d-93de-503c63e15317',
        merkleDropMinterV2_1: 'b64e7a8b-a2cc-4411-805a-46f290ff03c4',
        rangeEditionMinterV2_1: 'b6a24e84-7e9a-4a6b-b16a-15a430f825cb',
        soundSwapV1_1: '13cc2b31-214d-4228-bb7f-e2d5559a6912',
        soundSwap:
          ENVIRONMENT === 'staging'
            ? '0ba7f159-41f6-45f7-88df-ddc70e7cd1a1'
            : '76586c78-08b3-4da8-a2c1-56114a3a7b39',
      };
    case 'OPTIMISM_GOERLI':
      return {
        soundSwap: '9977373a-a61c-4224-8844-ccb93fa9b1d0',
        rangeEditionMinterV2: '6a06c8fc-f4e1-405a-a272-5cb41823aadc',
        merkleDropMinterV2: 'dbbbfc50-5b11-42b1-962a-546795bd3853',
        merkleDropMinterV2_1: '7f64898e-4887-47c9-b59c-406825c86695',
        rangeEditionMinterV2_1: '74011092-4139-45e8-aa7e-bef84c020a12',
        soundSwapV1_1: '5475d794-a9cb-4352-bc72-be196f897366',
        minterAdapter: null,
      };
    case 'MAINNET':
      return {
        soundSwap: 'fc3a1f88-2e2e-4353-8b68-712eca50cb67',
        minterAdapter: '996d2196-7b7a-4068-8903-950f3dedf8f3',
        merkleDropMinterV2: 'b452ecca-badb-4895-b188-c3e627628ecb',
        rangeEditionMinterV2: '9f89ba3b-be6d-46e3-ab74-a63d6050c114',
        merkleDropMinterV2_1: 'd059ac5e-5b3f-40f5-bbd8-ea5b990c2f02',
        rangeEditionMinterV2_1: 'b81e74f5-9ebf-49e1-8058-683d8c6f3cc3',
        soundSwapV1_1: 'e9952a36-3b55-4988-be85-529d3e59958c',
      };
    case 'OPTIMISM':
      return {
        rangeEditionMinterV2: 'c46cb469-fa68-4f37-a1ed-dd0ddea083e9',
        soundSwap: '1062ac3c-457e-4e3e-99fa-ff68ab870c37',
        merkleDropMinterV2: '62c8f4f9-9713-4604-8ce3-817772a6d3c7',
        merkleDropMinterV2_1: 'a57b92ba-56d4-47b0-91c9-17539d3fafd8',
        rangeEditionMinterV2_1: '05e24716-4075-4517-97fa-2c36864ffa01',
        soundSwapV1_1: 'c6a51aa8-6ee8-4b5c-8706-6ecc19c8cf89',
        minterAdapter: null,
      };
    default:
      throw new Error(`Unexpected chain ${chain}`);
  }
}

export const CROSSMINT_ENVIRONMENT = DEPLOY_ENVIRONMENT === 'production' ? 'production' : 'staging';

// BigInt("0x" + crypto.createHash("SHA256").update("crossmint-credit-card").digest("hex"))
export const CROSSMINT_CC_ATTRIBUTION_ID =
  '23334387490496220902756087083339141532635006782258954522352602686392870671321';

// see https://github.com/ethereum-optimism/optimism/blob/develop/packages/contracts-bedrock/deployments/mainnet/L1StandardBridgeProxy.json
export function getOpL1StandardBridgeAddress(chainId: number) {
  const chain = getChainName(chainId);

  switch (chain) {
    case 'GOERLI':
      return '0x636Af16bf2f682dD3109e60102b8E1A089FedAa8';
    case 'MAINNET':
      return '0x99C9fc46f92E8a1c0deC1b1747d010903E884bE1';
    default:
      throw new Error(`Unexpected chain for l1 optimism bridge ${chain}`);
  }
}

export const GRID_STYLE = clsx(
  'grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5',
);

export const GRID_CAROUSEL_INITIAL_PAGE = 10;

export const COVER_IMAGE_BACKGROUND_SRC =
  'https://d16zqvkniqf1rn.cloudfront.net/playlist_cover_background.png';

export const SOMETHING_WENT_WRONG_REACH_OUT_TO_SOUND_TEAM =
  'Something went wrong. Please reach out to the Sound team before trying again.';

export const TRY_AGAIN_UPLOAD = 'An issue occurred while uploading you song, please try again';

export const SoundContractInterfaceIds = {
  ISoundEditionV1: '0x50899e54',
  ISoundEditionV1_2: '0xa176eca6',
  IMinterModule: '0x37c74bd8',
  IFixedPriceSignatureMinter: '0xa61bd96f',
  IMerkleDropMinter: '0x89691c4c',
  IEditionMaxMinter: '0xa7ea8688',
  IRangeEditionMinter: '0x4d4a2e35',
  IMinterModuleV2: '0xf8ccd08e',
  IFixedPriceSignatureMinterV2: '0x0f713f15',
  IMerkleDropMinterV2: '0x5e9a2e5f',
  IEditionMaxMinterV2: '0x6ee3f258',
  IRangeEditionMinterV2: '0x84435ae5',
  IMinterModuleV2_1: '0x09d98f1e',
  IFixedPriceSignatureMinterV2_1: '0x32c3f8e7',
  IMerkleDropMinterV2_1: '0x6328e9ad',
  IEditionMaxMinterV2_1: '0x535135aa',
  IRangeEditionMinterV2_1: '0xb9f19d17',
  ISAM: '0xa3c2dbc7',
  ISAMV1_1: '0x212580d2',
  ISoundEditionV2: '0x7888cfe1',
  ISuperMinter: '0x1459ad68',
} as const;

export const POST_MAX_CHARACTERS = 280;
